<template>
  <el-config-provider namespace="ui">
    <mobile-header v-if="store.is_mobile && !isRegister" />
    <router-view v-if="!me_loading" />
    <global-drawer ref="globalDrawer" />
    <tipalti-mfa-dialog ref="mfaDialogRef" />
  </el-config-provider>
</template>

<script setup>
import { watch, onMounted, onBeforeUnmount, ref, computed, provide } from 'vue';
import router from './router';
import { useGlobalStore } from './stores/GlobalStore';
import { useRoute } from 'vue-router';
import MobileHeader from '@shared/components/MobileHeader.vue';
import GlobalDrawer from '@shared/components/GlobalDrawer.vue';
import TipaltiMfaDialog from '@/components/TipaltiMfaDialog.vue';
import ajax from '@/utils/Ajax';

const route = useRoute();
const mfaDialogRef = ref(null);
provide('tipaltiMfaVerify', () => mfaDialogRef.value);
const store = useGlobalStore();
const me_loading = ref(true);
const is_login = ref(false);

// Password pwned check — sephora only
const checkPasswordPwned = async (password) => {
  try {
    const res = await ajax.post('/check-password-pwned', { password });
    const { is_pwned, pwn_service_working } = res.data.data;
    if (!pwn_service_working) return true; // graceful degradation — allow
    return !is_pwned; // true = safe, false = compromised
  } catch (err) {
    return true; // network error — graceful degradation, allow
  }
};
provide('checkPasswordPwned', checkPasswordPwned);

const isRegister = computed(() => {
  let rou = router.currentRoute.value.meta;
  return rou.login || rou.heading === 'Register' || rou.heading === 'Login';
});

// watch router name
const pageHeading = computed(() => router.currentRoute.value.meta.heading || '');
watch(pageHeading, (heading) => {
  document.body.className = `page-${router.currentRoute.value.name}`;

  // scroll to top
  window.scrollTo(0, 0);
});

store.setIsMobile(window.innerWidth < 728);
store.setIsTablet(window.innerWidth < 1180);

let resizeTimeout;
const handleResize = () => {
  const vh = window.innerHeight * 0.01; // 1vh
  document.documentElement.style.setProperty('--real-vh', `${vh}px`);

  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    const newIsMobile = window.innerWidth < 728;
    const newIsTablet = window.innerWidth < 1180;

    if (store.is_mobile !== newIsMobile) {
      store.setIsMobile(newIsMobile);
    }
    if (store.is_tablet !== newIsTablet) {
      store.setIsTablet(newIsTablet);
    }
  }, 200); // 優化resize事件
};

const checkLogin = () => {
  is_login.value = window.localStorage && window.localStorage.getItem('token');
  let isRegisterGoogleToken = window.localStorage.getItem('CREATOR_PROTAL_Registor_Google');
  let isRegisterGetSocial = window.localStorage.getItem('REATOR_PROTAL_register_go_out');
  let isRegisterGoogleGoStep2 = window.localStorage.getItem('is_google_go_step2');
  let isRegisterVer = window.localStorage.getItem('CREATOR_PROTAL_is_verifity');
  let isRegisterGetSocialStep2 = window.localStorage.getItem('CREATOR_PROTAL_is_more_social');

  if(isRegisterVer ||isRegisterGetSocialStep2){
    me_loading.value = false;
    return;
  }
  const isPublicPage = ['/pages/motom-terms-of-use', '/pages/motom-privacy-policy','/pages/sephora-storefront-affiliate-terms'].includes(window.location.pathname);
  if (is_login.value !== null && !isRegisterGoogleToken && !isRegisterGetSocial && !isRegisterGoogleGoStep2 && !isPublicPage) {
    store
      .getUserMe()
      .then((user) => {
        store.user = user;
        // user_status = 100 email verified
        // if user_status is not 100, redirect to login page
        if (route.name === undefined) {
          me_loading.value = false;
          return;
        }

        if (window.location.href.includes('register') && route.name !== 'register') {
          window.location.href = '/register';
          me_loading.value = false;
          return;
        }

        if (
          store.user.user_status !== 100 &&
          route.name !== 'register' &&
          !route.meta.login &&
          !window.location.href.includes('register')
        ) {
          store.clearToken();
          window.location.href = '/login';
        } else if (route.meta.login) {
          window.location.href = '/';
        }
        setTimeout(() => {
          me_loading.value = false;
        }, 100);
      })
      .catch((err) => {
        me_loading.value = false;
        if (route.name !== 'invite' && route.name !== 'register' && route.name !== undefined) {
          window.location.href = '/login';
        }
      });
  } else {
    me_loading.value = false;
  }
};

onMounted(() => {
  checkLogin();
  handleResize();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss">
@import '@/assets/scss/element.scss';
@import '@/assets/scss/fonts.scss';
@import '@/assets/scss/reset.scss';
@import '@/assets/scss/register.scss';

// @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap');

:root {
  --font: 'Franklin Gothic Book', sans-serif;
  // --font-fg: 'Franklin Gothic', sans-serif;
  --font-demi: 'Franklin Gothic Demi', sans-serif;
  --font-med: 'Franklin Gothic Medium', sans-serif;
  // --font-ms: 'Montserrat', sans-serif;
  // --font-dm: 'DM Serif Display', sans-serif;

  /* Franklin Gothic Book */
  --book-10: 400 10px/14px var(--font);
  --book-12: 400 12px/16px var(--font);
  --book-14: 400 14px/18px var(--font);
  --book-16: 400 16px/20px var(--font);
  --book-18: 400 18px/22px var(--font);
  --book-20: 400 20px/24px var(--font);
  --book-22: 400 22px/26px var(--font);

  /* Franklin Gothic Medium */
  --medium-10: 400 10px/14px var(--font-med);
  --medium-12: 400 12px/16px var(--font-med);
  --medium-14: 400 14px/18px var(--font-med);
  --medium-16: 400 16px/20px var(--font-med);
  --medium-18: 400 18px/22px var(--font-med);
  --medium-20: 400 20px/24px var(--font-med);
  --medium-22: 400 22px/26px var(--font-med);
  --medium-24: 400 24px/28px var(--font-med);
  --medium-26: 400 26px/30px var(--font-med);
  --medium-28: 400 28px/32px var(--font-med);
  --medium-30: 400 30px/34px var(--font-med);
  --medium-32: 400 32px/36px var(--font-med);
  --medium-34: 400 34px/38px var(--font-med);
  --medium-36: 400 36px/40px var(--font-med);

  /* Franklin Gothic Demi */
  --demi-10: 400 10px/14px var(--font-demi);
  --demi-12: 400 12px/16px var(--font-demi);
  --demi-14: 400 14px/18px var(--font-demi);
  --demi-16: 400 16px/20px var(--font-demi);
  --demi-18: 400 18px/22px var(--font-demi);
  --demi-20: 400 20px/24px var(--font-demi);
  --demi-22: 400 22px/26px var(--font-demi);
  --demi-24: 400 24px/28px var(--font-demi);
  --demi-26: 400 26px/30px var(--font-demi);
  --demi-28: 400 28px/32px var(--font-demi);
  --demi-30: 400 30px/34px var(--font-demi);
  --demi-32: 400 32px/36px var(--font-demi);
  --demi-34: 400 34px/38px var(--font-demi);
  --demi-36: 400 36px/40px var(--font-demi);

  --PB-100: #c92d3b;
  --PB-80: lighten(#c92d3b, 20%);
  --PB-60: lighten(#c92d3b, 40%);
  --PB-40: lighten(#c92d3b, 60%);
  --PB-20: lighten(#c92d3b, 80%);

  --Brand-primary: #c92d3b;
  --Brand-secondary: #f2827f;
  --Brand-orange: #f2827f;
  --Brand-purple: #7e86bf;
  --Brand-grey: #eeeeee;

  --Red-10: #ffebeb;
  --Red-30: #cf112c;
  --Yellow-10: #fff5d5;
  --Yellow-30: #ffc003;
  --Blue-10: #d3e1fe;
  --Blue-30: #136bea;
  --Green-10: #e8f8f2;
  --Green-30: #008048;

  --Black-100: #000000;
  --Black-90: #000000;
  --Black-80: #666666;
  --Black-70: #666666;
  --Black-60: #888888;
  --Black-50: #888888;
  --Black-40: #cccccc;
  --Black-30: #eeeeee;
  --Black-20: #f6f6f8;
  --Black-10: #f6f6f8;
  --Black-00: #ffffff;

  --Black-100-rgba: 0, 0, 0;
  --Black-90-rgba: 51, 51, 51;
  --Black-60-rgba: 130, 130, 130;
  --Black-00-rgba: 255, 255, 255;

  --Neu-00: #ffffff;
  --Neu-10: #f6f8fc;
  --Neu-20: #f1f4f9;
  --Neu-30: #e2e8f0;
  --Neu-40: #cbd4e1;
  --Neu-50: #94a3b8;
  --Neu-60: #64748b;
  --Neu-70: #475569;
  --Neu-80: #27364b;
  --Neu-90: #1e2a3b;
  --Neu-100: #0f1a2a;

  --Spotify: #1db954;
  --Facebook-new: #1877f2;
  --Facebook-old: #3b5998;
  --Insta: #c32aa3;
  --Pinterest: #e60023;
  --Twitter: #1da1f2;
  --X-twitter: #000000;
  --YouTube: #ff0000;
  --Snapchat: #fffc00;
  --Google: #0f9d58;
  --Linktree: #3ddc84;
  --Apple-iOS: #8e8e93;
  --Win: #0078d4;
  --TikTok-Pink: #ee1d52;
  --TikTok-Blue: #69c9d0;
  --TikTok-dark: #010101;

  --Text-brand: var(--Black-100);
  --Text-primary: var(--Black-100);
  --Text-secondary: var(--Black-80);
  --Text-default: var(--Black-100);
  --Text-disabled: var(--Black-80);
  --Text-invert: var(--Black-00);
  --Text-danger: var(--Red-30);
  --Text-warning: var(--Red-30);
  --Text-safe: var(--Green-30);
  --Text-link: var(--Blue-30);

  --Border-primary: var(--Black-40);
  --Border-secondary: var(--Black-40);
  --Border-dark: var(--Black-100);
  --Border-light: var(--Black-00);
  --Border-default: var(--Black-30);
  --Border-hover: var(--Black-40);
  --Border-disable: var(--Black-40);
  --Border-neu: var(--Black-40);
  --Border-danger: var(--Red-30);
  --Border-Warning: var(--Yellow-30);
  --Border-safe: var(--Green-30);
  --Border-brand: var(--Brand-primary);

  --Surface-primary: #c92d3b;
  --Surface-primary-hover: var(--Neu-70);
  --Surface-secondary: #f2827f;
  --Surface-dark: var(--Black-100);
  --Surface-light: var(--Black-00);
  --Surface-neu: var(--Neu-20);
  --Surface-alert: var(--Red-30);
  --Surface-default: var(--Black-20);
  --Surface-hover: var(--Black-30);
  --Surface-disable: var(--Black-30);
  --Surface-tertiary-selected: var(--Black-20);
  --Surface-danger: var(--Red-10);
  --Surface-safe: var(--Green-10);
  --Surface-warn: var(--Yellow-10);
  --Surface-normal: var(--Blue-10);

  --Bg-dark: var(--Black-90);
  --Bg-light: var(--Black-00);
  --Bg-01: var(--Black-10);
  --Bg-02: var(--Black-20);
  --Bg-03: var(--Black-30);

  --bot-1dp: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
  --bot-2dp: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  --bot-4dp: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  --bot-6dp: 0px 6px 8px 0px rgba(0, 0, 0, 0.1);

  --top-1dp: 0px -1px 2px 0px rgba(0, 0, 0, 0.1);
  --top-2dp: 0px -2px 4px 0px rgba(0, 0, 0, 0.1);
  --top-4dp: 0px -4px 8px 0px rgba(0, 0, 0, 0.1);
  --top-6dp: 0px -6px 8px 0px rgba(0, 0, 0, 0.1);

  --right-1dp: 1px 0px 2px 0px rgba(0, 0, 0, 0.1);
  --right-2dp: 2px 0px 4px 0px rgba(0, 0, 0, 0.1);
  --right-4dp: 4px 0px 8px 0px rgba(0, 0, 0, 0.1);
  --right-6dp: 6px 0px 8px 0px rgba(0, 0, 0, 0.1);

  --left-1dp: -1px 0px 2px 0px rgba(0, 0, 0, 0.1);
  --left-2dp: -2px 0px 4px 0px rgba(0, 0, 0, 0.1);
  --left-4dp: -4px 0px 8px 0px rgba(0, 0, 0, 0.1);
  --left-6dp: -6px 0px 8px 0px rgba(0, 0, 0, 0.1);

  --card: 0px 0 2px 1px rgba(0, 0, 0, 0.1);
  --pop-medium: 2px 4px 30px 1px rgba(0, 0, 0, 0.15);
  --pop-light: 2px 4px 20px 0px rgba(0, 0, 0, 0.1);
  --neno-01: 0px 4px 8px 0px rgba(241, 101, 41, 0.35), 1px 2px 4px 0px rgba(249, 163, 38, 0.32);

  --mbg-bttb: 348.82deg, var(--Red-30) 8.25%, var(--Yellow-30) 91.52%;
  --mbg-bttf: 102.8deg, var(--Red-30) 6.94%, var(--Yellow-30) 91.73%;
  --mbg-bttl: 168.82deg, var(--Red-30) 11.16%, var(--Yellow-30) 91.53%;

  --font-xxxl: 32px;
  --font-xxl: 24px;
  --font-xl: 22px;
  --font-l: 20px;
  --font-md: 18px;
  --font-m: 16px;
  --font-s: 14px;
  --font-xs: 12px;
  --font-xxs: 10px;

  --font-lh-xxxl: 34px;
  --font-lh-xxl: 26px;
  --font-lh-xl: 24px;
  --font-lh-l: 22px;
  --font-lh-md: 20px;
  --font-lh-m: 18px;
  --font-lh-s: 16px;
  --font-lh-xs: 14px;
  --font-lh-xxs: 12px;

  --font-semi: 600;
  --font-medium: 500;
  --font-regular: 400;
  --font-light: 300;

  --spacing-01: 2px;
  --spacing-02: 4px;
  --spacing-03: 8px;
  --spacing-04: 12px;
  --spacing-05: 16px;
  --spacing-06: 24px;
  --spacing-07: 32px;
  --spacing-08: 36px;
  --spacing-09: 40px;
  --spacing-10: 48px;
  --spacing-11: 64px;

  --register_max_width: 550px;
  --radius: 4px;
  --radius-small: 2px;
  --radius-medium: 6px;
  --radius-big: 16px;
  --radius-round: 50%;
  --radius-large: 8px;

  --mobile-header-height: 40px;
  --mobile-footer-height: 58px;
  --analytics-mob-padding: 12px;

  --dialog-max-width: 616px;
}

html {
  -webkit-overflow-scrolling: touch;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  background-color: var(--Bg-01);
}

body {
  font: var(--book-14);

  /* Font smoothing */
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--Bg-01);

  &.disabled-scroll {
    overflow: hidden !important;
  }
}

* {
  box-sizing: border-box;
}

#app {
  min-height: 100vh;

  .w-100 {
    width: 100%;
  }

  .tag-product-drawer .tags .ui-tag.ui-tag--primary {
    padding: 5px 10px;
    font: var(--medium-12);
    color: var(--Text-secondary);
    background-color: var(--Neu-20);
    cursor: pointer;
    transition: all .2s ease;

    &:hover {
      background-color: var(--Neu-30);
      color: var(--Text-primary);
    }
  }

  .login-container {
    padding: 0 !important;
    @media (max-width: $mobile_size) {
      display: flex !important;
    }
    .ui-main {
      background: var(--Bg-01);
    }
  }

  .sortlink-checkbox-box {
    .ui-checkbox {
      height: 16px;
      margin-top: 4px;

      .svg-icon {
        width: 16px;
        height: 16px;
      }
    }

    .ui-tooltip__trigger {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 4px;
    }
  }

  .report-grid-data {
    @media (min-width: $mobile_size) {
      display: grid;
      border: solid 1px var(--Black-40);

      .grid-box {
        border-right: solid 1px var(--Black-40);
        padding: 8px 16px; //24px 16px;

        .value {
          font: var(--demi-24);
        }

        .label-big {
          font: var(--medium-16);
        }

        &:last-child {
          border-right: none;
        }
      }
    }

    .grid-box {
      .value {
        color: var(--Black-100);

        svg {
          margin-left: 10px;
          width: 10px;
          height: 10px;
          fill: var(--Green-30);

          &.down {
            fill: var(--Red-30);
            transform: rotate(90deg);
          }
        }
      }

      .label-big {
        color: var(--Text-secondary);
      }
    }
  }
}

svg:focus {
  outline: none !important;
}

a {
  color: var(--Text-link);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.main-content {
  background-color: var(--Bg-light);
  height: calc(100vh - 10px);
  margin-top: 10px;
  border-radius: 16px 0 0 0;
  box-shadow: var(--card);

  @media screen and (max-width: $mobile_size) {
    border-radius: 0;
    box-shadow: none;
  }
}

.custom-select-picker {
  //transform: translateY(-12px);
  .ui-popper__arrow {
    display: none;
  }
}

.alert-modal {
  padding: 24px;

  i {
    display: block;
    height: 68px;
    width: 68px;
    margin: 0 auto 24px;
    color: var(--Blue-30);

    svg {
      display: block;
      height: 100%;
      width: 100%;
    }
  }

  h2 {
    font: var(--medium-18);
    text-align: center;
    margin-bottom: 24px;
    color: var(--Black-100);
  }

  .ui-button {
    margin: 0 auto;
    min-width: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.alert-modal-2 {
  .content {
    text-align: left;

    h2 {
      font: var(--medium-18);
      text-align: left;
      margin-bottom: 16px;
    }

    p {
      padding: 0 0 36px;
      text-align: left;
    }

    & + footer {
      display: flex;

      .ui-button--text {
        min-width: 130px;
        margin-right: 16px;
        text-align: center;
      }

      .ui-button--primary {
        flex: 1;
      }
    }
  }
}

#app {
  .report-grid-data-2 .type-box .type .value,
  .custom-th .ico.sort.asc,
  .custom-th .ico.sort.desc,
  .the-type .value {
    color: var(--Black-100);
  }

  .custom-th .ico.sort.asc svg,
  .custom-th .ico.sort.asc .svg-icon,
  .custom-th .ico.sort.desc svg,
  .custom-th .ico.sort.desc .svg-icon {
    fill: var(--Black-100);
  }

  .ui-navi ul li a,
  .ui-navi .link {
    color: var(--Black-100);
    background-color: var(--Black-00);
  }

  .ui-navi ul li a:hover,
  .ui-navi .link:hover {
    color: var(--Black-70);
  }

  .ui-navi ul li a.router-link-active,
  .ui-navi ul li a.router-link-exact-active,
  .ui-navi ul li a.link-selected,
  .ui-navi .link.router-link-active,
  .ui-navi .link.router-link-exact-active,
  .ui-navi .link.link-selected {
    color: var(--Black-100);

    &::before {
      background-color: var(--Black-100);
    }
  }

  .announcements-block .btn-pick {
    border-width: 2px;
  }

  .ui-pagenavi .bar {
    background-color: var(--Black-30);
    border-color: var(--Black-30);
  }

  .storefront-page-navi {
    background-color: var(--Black-00);
  }

  .mobile-footer-navi ul li {
    a {
      color: var(--Black-100);
      background-color: var(--Black-00);
      max-width: 58px;
      margin: 0 auto;

      &:hover {
        color: var(--Black-70);
      }
    }

    &.actived a {
      background-color: var(--Black-100);
      color: var(--Black-00);
    }
  }

  .ui-button--small {
    border-radius: var(--ui-border-radius-base);
  }

  .mobile-header .center .select-storefront .ui-dropdown {
    pointer-events: none;
  }

  .profile-dialog .ui-form-item .prepend {
    background-color: var(--Black-30);
  }

  .tag-product-drawer .input-box > header h2,
  .tag-product-drawer .list-box .info-head .info h2,
  .home-page .section-left .summary-block h4,
  .message-sidebar,
  .add-brand-connect-list-box .el-collapse-item__header,
  .add-brand-connect-list-box .ui-collapse-item__header,
  .announcements-wrap .swiper,
  .announcements-block .swiper,
  .mobile-header .center .select-storefront .dropdown-trigger-image,
  .mobile-header .center .select-storefront .ui-dropdown svg,
  .analytics-header .left .ui-select,
  .analytics-header .left .arrow-icon,
  .global-sidebar .sidebar-srcoll nav > ul > li > a i,
  .global-sidebar .sidebar-srcoll nav .sub-menu-tigger,
  .global-sidebar .sidebar-srcoll nav .sub-menu-tigger + div {
    display: none !important;
  }

  .add-brand-connect-list-box .ui-collapse-item__wrap {
    display: block !important;
  }

  .tag-product-drawer .input-box > header .link {
    margin-top: 0;
  }

  .add-brand-connect-list-box .connect-box {
    padding: 0;
  }

  .summary-block .start-block .start-small-block .svg-icon.svg-icon__todo {
    fill: var(--Black-100);
  }
}


.profile-data-select-picker {
  .ui-picker-panel__icon-btn.d-arrow-right,
  .d-arrow-left.ui-picker-panel__icon-btn,
  .ui-date-picker__header > span:nth-child(2) {
    display: none !important;
  }
}
</style>
